<template>
  <section class="hx-pagina" aria-labelledby="historial-titulo" data-testid="historial-buscar">
    <div class="hx-contenido">
      <header class="hx-cabecera">
        <h1 id="historial-titulo" class="hx-titulo">Historial</h1>
        <p class="hx-sub">Busca el auto por placa, número de serie, cliente, teléfono o folio de orden.</p>
      </header>

      <div class="hx-buscador" @click="alClicBuscador">
        <AppIcon name="search" size="h-5 w-5" class="hx-buscador-icono" />
        <input
          id="historial-q"
          ref="campo"
          :value="texto"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="String(listaVisible)"
          :aria-controls="listaVisible ? 'historial-opciones' : null"
          :aria-activedescendant="listaVisible && activo >= 0 ? 'historial-opcion-' + activo : null"
          aria-label="Buscar por placa, serie, cliente, teléfono o folio"
          aria-describedby="historial-atajo"
          placeholder="Placa, serie, cliente, teléfono o folio"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          enterkeyhint="search"
          data-testid="historial-campo"
          @input="alEscribir"
          @keydown="alTeclear"
        />
        <button
          v-if="texto"
          type="button"
          class="hx-borrar"
          aria-label="Borrar búsqueda"
          data-testid="historial-borrar"
          @click.stop="borrar"
        >
          <AppIcon name="x-mark" size="h-4 w-4" />
        </button>
        <kbd class="hx-atajo" aria-hidden="true" title="Atajo de teclado: /">/</kbd>
        <span id="historial-atajo" class="hx-sr">Atajo: tecla diagonal. Flechas para recorrer los resultados y Enter para abrir.</span>
      </div>

      <p class="hx-sr" role="status" aria-live="polite" data-testid="historial-anuncio">{{ anuncio }}</p>

      <!-- Resultados -->
      <div
        v-if="enBusqueda && !cerrada"
        class="hx-tarjeta hx-resultados"
        :class="{ 'is-actualizando': cargando && resultados && !error }"
        :aria-busy="cargando ? 'true' : null"
        data-testid="historial-resultados"
      >
        <div v-if="cargando" class="hx-progreso" role="progressbar" aria-label="Buscando"></div>

        <div v-if="error" class="hx-error" role="alert" data-testid="historial-error">
          <span>No pudimos buscar. Revisa la conexión e inténtalo de nuevo.</span>
          <button type="button" class="rm-btn rm-btn-secundario" data-testid="historial-reintentar" @click="buscarAhora">Reintentar</button>
        </div>

        <div v-else-if="!resultados" class="hx-esqueleto" aria-hidden="true">
          <span v-for="n in 3" :key="n" class="hx-skel hx-skel-fila"></span>
        </div>

        <div v-else-if="!opciones.length" class="hx-vacio" data-testid="historial-vacio">
          <p class="hx-vacio-titulo">No encontramos nada con «{{ consulta }}».</p>
          <p>Revisa la placa o el folio, o prueba con el apellido del cliente o su teléfono.</p>
        </div>

        <template v-else>
          <p v-if="aviso" class="hx-aviso" role="status" data-testid="historial-aviso">
            <AppIcon name="exclamation" size="h-4 w-4" />
            <span>{{ aviso }}</span>
          </p>
          <div id="historial-opciones" role="listbox" aria-label="Resultados de la búsqueda">
            <div
              v-for="grupo in grupos"
              :key="grupo.id"
              role="group"
              :aria-labelledby="'historial-grupo-' + grupo.id"
              :data-testid="'historial-grupo-' + grupo.id"
            >
              <div :id="'historial-grupo-' + grupo.id" class="hx-grupo" role="presentation" data-testid="historial-grupo-titulo">{{ grupo.titulo }}</div>
              <div
                v-for="op in grupo.opciones"
                :id="'historial-opcion-' + op.indice"
                :key="op.clave"
                role="option"
                :aria-selected="String(op.indice === activo)"
                class="hx-resultado"
                :class="{ 'is-activo': op.indice === activo }"
                :data-testid="'historial-opcion-' + op.tipo"
                @mousedown.prevent
                @mousemove="activo = op.indice"
                @click="elegir(op)"
              >
                <!-- Cliente -->
                <template v-if="op.tipo === 'cliente'">
                  <span class="hx-avatar" aria-hidden="true">{{ inicialesDe(op.dato.nombre) }}</span>
                  <span class="hx-resultado-datos">
                    <b class="hx-resultado-titulo">{{ nombre(op.dato.nombre) }}</b>
                    <span class="hx-resultado-meta">{{ metaCliente(op.dato) }}</span>
                  </span>
                </template>
                <!-- Auto -->
                <template v-else-if="op.tipo === 'auto'">
                  <AppPlaca :placa="op.dato.placa" chica />
                  <span class="hx-resultado-datos">
                    <b class="hx-resultado-titulo">{{ tituloAuto(op.dato) }}</b>
                    <span class="hx-resultado-meta">
                      {{ metaAuto(op.dato) }}
                      <template v-if="op.dato.coincidencia === 'serie' && op.dato.serie"> · Serie <span class="hx-mono">{{ op.dato.serie }}</span></template>
                    </span>
                  </span>
                </template>
                <!-- Orden -->
                <template v-else>
                  <span class="hx-avatar hx-avatar-orden" aria-hidden="true"><AppIcon name="clipboard" size="h-4 w-4" /></span>
                  <span class="hx-resultado-datos">
                    <b class="hx-resultado-titulo"><span class="hx-mono">{{ op.dato.folio }}</span> · {{ oracion(op.dato.detalle) || 'Sin descripción' }}</b>
                    <span class="hx-resultado-meta">
                      <template v-if="op.dato.vehiculo">{{ descripcion(op.dato.vehiculo.descripcion) }}<template v-if="op.dato.vehiculo.placa"> · {{ op.dato.vehiculo.placa }}</template> · </template>{{ estadoTexto(op.dato.estado) }}
                    </span>
                  </span>
                </template>
                <span class="hx-cuando">
                  <template v-if="op.tipo === 'orden'">
                    <template v-if="op.dato.recibida">Recibida<br /><b>{{ fecha(op.dato.recibida) }}</b></template>
                  </template>
                  <template v-else-if="op.dato.ultimaVisita">Última visita<br /><b>{{ fecha(op.dato.ultimaVisita) }}</b></template>
                  <template v-else>Sin visitas</template>
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Sin búsqueda -->
      <div v-else-if="!enBusqueda" class="hx-inicio">
        <section class="hx-tarjeta" aria-labelledby="historial-recientes-titulo" data-testid="historial-recientes">
          <h2 id="historial-recientes-titulo" class="hx-tarjeta-titulo">Vistos recientemente</h2>
          <ul v-if="recientes.length" class="hx-lista-recientes">
            <li v-for="auto in recientes" :key="auto._id">
              <a
                class="hx-reciente"
                :href="hrefExpediente(auto._id)"
                data-testid="historial-reciente"
                @click="alClicReciente($event, auto._id)"
              >
                <AppPlaca :placa="auto.placa" chica />
                <span class="hx-resultado-datos">
                  <b class="hx-resultado-titulo">{{ descripcion(auto.descripcion) }}</b>
                </span>
                <AppIcon name="chevron-right" size="h-4 w-4" class="hx-reciente-flecha" />
              </a>
            </li>
          </ul>
          <p v-else class="hx-texto-suave">Los autos que abras aparecerán aquí para volver a ellos con un clic.</p>
        </section>

        <section class="hx-tarjeta" aria-labelledby="historial-ejemplos-titulo" data-testid="historial-ejemplos">
          <h2 id="historial-ejemplos-titulo" class="hx-tarjeta-titulo">Qué puedes buscar</h2>
          <dl class="hx-ejemplos">
            <div><dt>Placa</dt><dd>Con o sin guiones: <span class="hx-mono">vnp482</span> encuentra <span class="hx-mono">VNP-482-B</span></dd></div>
            <div><dt>Número de serie</dt><dd>Completo o una parte, desde 4 caracteres</dd></div>
            <div><dt>Cliente</dt><dd>Nombre y apellidos en cualquier orden, con o sin acentos</dd></div>
            <div><dt>Teléfono</dt><dd>Desde 4 dígitos, con o sin espacios</dd></div>
            <div><dt>Folio de orden</dt><dd><span class="hx-mono">AAA-0006891</span>, <span class="hx-mono">aaa-6891</span> o <span class="hx-mono">6891</span></dd></div>
          </dl>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
/*
 * Historial › Buscar. Un solo campo para cliente, placa, serie, teléfono y
 * folio (GET /api/historial/buscar). Espera ~250 ms después de teclear,
 * pide 2 caracteres como mínimo y descarta las respuestas que llegan tarde.
 * Resultados agrupados y navegables con ↑ ↓ Enter y Esc (patrón combobox).
 *
 * La búsqueda vive en la URL (?q=): el padre la recibe con `consulta` y la
 * devuelve en `q`, así "Cambiar de auto" o Atrás regresan a la misma lista.
 */
import Historial from '@/services/historial'
import '@/assets/css/historial.css'
import AppIcon from '@/components/ui/AppIcon.vue'
import AppPlaca from '@/components/ui/AppPlaca.vue'
import { formatearFechaNumerica, telefonosDe, textoOracion } from '@/utils/formato'
import { descripcionDeAuto, esClicSimple, estadoDeOrden, iniciales, leerRecientes, nombreDeAuto, nombreLimpio } from '@/utils/historial'

export const ESPERA_BUSQUEDA = 250
export const MINIMO_CARACTERES = 2

const TITULOS_GRUPO = {
  clientes: 'Clientes',
  autos: 'Autos',
  autosDeClientes: 'Autos de estos clientes',
  ordenes: 'Órdenes',
}

const plural = (n, singular, varios) => `${n} ${n === 1 ? singular : varios}`

function esCampo(elemento) {
  return Boolean(elemento && (elemento.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(elemento.tagName)))
}

// BaseModal (role=dialog, aria-modal) o un v-dialog de Vuetify abiertos
function hayModalAbierto() {
  return Boolean(document.querySelector('[aria-modal="true"], .v-dialog--active'))
}

export default {
  name: 'HistorialBuscar',
  components: { AppIcon, AppPlaca },
  props: {
    q: { type: String, default: '' },
  },
  emits: ['consulta'],
  data() {
    return {
      texto: '',
      consulta: '',
      resultados: null,
      cargando: false,
      error: false,
      activo: -1,
      cerrada: false,
      aviso: '',
      anuncio: '',
      recientes: [],
    }
  },
  computed: {
    enBusqueda() {
      return this.texto.trim().length >= MINIMO_CARACTERES
    },
    opciones() {
      const r = this.resultados
      if (!r) return []
      const lista = []
      const agregar = (tipo, grupo) => (dato) =>
        lista.push({ tipo, grupo, dato, indice: lista.length, clave: `${grupo}-${dato._id || lista.length}` })
      r.clientes.forEach(agregar('cliente', 'clientes'))
      r.autos.filter((a) => a.coincidencia !== 'cliente').forEach(agregar('auto', 'autos'))
      r.autos.filter((a) => a.coincidencia === 'cliente').forEach(agregar('auto', 'autosDeClientes'))
      r.ordenes.forEach(agregar('orden', 'ordenes'))
      return lista
    },
    grupos() {
      return Object.keys(TITULOS_GRUPO)
        .map((id) => ({ id, titulo: TITULOS_GRUPO[id], opciones: this.opciones.filter((op) => op.grupo === id) }))
        .filter((grupo) => grupo.opciones.length)
    },
    listaVisible() {
      return this.enBusqueda && !this.cerrada && !this.error && this.opciones.length > 0
    },
  },
  watch: {
    // La URL cambió desde fuera (menú, Atrás): se adopta esa búsqueda
    q(nuevo) {
      const valor = String(nuevo || '')
      if (valor === this.ultimaEmitida) return
      this.ultimaEmitida = valor
      this.texto = valor
      if (valor.trim().length >= MINIMO_CARACTERES) {
        this.buscarAhora()
      } else {
        this.reiniciar()
        this.recientes = leerRecientes()
      }
    },
  },
  created() {
    this.ultimaConsulta = 0
    this.temporizador = null
    this.ultimaEmitida = String(this.q || '')
  },
  mounted() {
    this.recientes = leerRecientes()
    if (this.ultimaEmitida) {
      this.texto = this.ultimaEmitida
      if (this.enBusqueda) this.buscarAhora()
    }
    document.addEventListener('keydown', this.alAtajo)
    this.$nextTick(this.enfocar)
  },
  beforeDestroy() {
    clearTimeout(this.temporizador)
    this.ultimaConsulta++
    document.removeEventListener('keydown', this.alAtajo)
  },
  methods: {
    enfocar() {
      const campo = this.$refs.campo
      if (!campo) return
      campo.focus()
      const fin = campo.value.length
      if (typeof campo.setSelectionRange === 'function') campo.setSelectionRange(fin, fin)
    },
    // Un clic en el marco (lupa, márgenes) enfoca el campo; dentro del campo
    // se deja al navegador para no mover el cursor ni perder la selección
    alClicBuscador(event) {
      if (event.target === this.$refs.campo) {
        this.cerrada = false
        return
      }
      this.enfocar()
    },
    alAtajo(event) {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return
      if (esCampo(event.target) || esCampo(document.activeElement) || hayModalAbierto()) return
      event.preventDefault()
      this.enfocar()
    },
    alEscribir(event) {
      this.texto = event.target.value
      this.aviso = ''
      this.cerrada = false
      clearTimeout(this.temporizador)
      this.temporizador = null
      const q = this.texto.trim()
      if (q.length < MINIMO_CARACTERES) {
        this.reiniciar()
        this.emitirConsulta('')
        return
      }
      // Volver a la consulta que ya está en pantalla (un espacio de más o
      // borrar lo último) no repite la búsqueda: se descarta la que estaba en
      // curso y la URL vuelve a esta consulta
      if (q === this.consulta && this.resultados && !this.error) {
        this.ultimaConsulta++
        this.cargando = false
        this.emitirConsulta(q)
        return
      }
      this.cargando = true
      this.temporizador = setTimeout(this.buscarAhora, ESPERA_BUSQUEDA)
    },
    async buscarAhora() {
      clearTimeout(this.temporizador)
      this.temporizador = null
      const q = this.texto.trim()
      if (q.length < MINIMO_CARACTERES) return
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.error = false
      this.cerrada = false
      this.emitirConsulta(q)
      try {
        const resultados = await Historial.buscar(q)
        if (consulta !== this.ultimaConsulta) return
        this.resultados = resultados
        this.consulta = q
        this.aviso = ''
        this.activo = this.opciones.length ? 0 : -1
        this.anuncio = this.opciones.length
          ? `${plural(this.opciones.length, 'resultado', 'resultados')}. Usa las flechas para recorrerlos.`
          : `Sin resultados para ${q}.`
      } catch (error) {
        if (consulta !== this.ultimaConsulta) return
        this.error = true
        this.anuncio = 'No pudimos buscar.'
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    reiniciar() {
      clearTimeout(this.temporizador)
      this.temporizador = null
      this.ultimaConsulta++
      this.resultados = null
      this.consulta = ''
      this.error = false
      this.cargando = false
      this.activo = -1
      this.cerrada = false
      this.aviso = ''
      this.anuncio = ''
    },
    borrar() {
      this.texto = ''
      this.reiniciar()
      this.emitirConsulta('')
      this.recientes = leerRecientes()
      this.$nextTick(this.enfocar)
    },
    emitirConsulta(q) {
      if (q === this.ultimaEmitida) return
      this.ultimaEmitida = q
      this.$emit('consulta', q)
    },
    alTeclear(event) {
      const total = this.opciones.length
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (this.cerrada && this.opciones.length && !this.error) {
          event.preventDefault()
          this.cerrada = false
          return
        }
        if (!this.listaVisible) return
        event.preventDefault()
        const paso = event.key === 'ArrowDown' ? 1 : -1
        const actual = this.activo < 0 ? (paso > 0 ? -1 : total) : this.activo
        this.activo = Math.min(Math.max(actual + paso, 0), total - 1)
        this.$nextTick(this.mostrarActivo)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.cerrada) {
          this.cerrada = false
          return
        }
        const pendiente = this.temporizador || (this.enBusqueda && this.texto.trim() !== this.consulta)
        if (pendiente || this.error) {
          this.buscarAhora()
        } else if (this.listaVisible) {
          this.elegir(this.opciones[this.activo >= 0 ? this.activo : 0])
        }
      } else if (event.key === 'Escape') {
        // Primero cierra la lista; con la lista cerrada, borra el texto
        if (this.listaVisible) {
          event.preventDefault()
          this.cerrada = true
          this.anuncio = 'Lista de resultados cerrada.'
          return
        }
        if (!this.texto) return
        event.preventDefault()
        this.borrar()
      }
    },
    mostrarActivo() {
      const opcion = this.$el.querySelector(`#historial-opcion-${this.activo}`)
      if (opcion && typeof opcion.scrollIntoView === 'function') opcion.scrollIntoView({ block: 'nearest' })
    },
    elegir(op) {
      if (!op) return
      this.aviso = ''
      if (op.tipo === 'cliente') {
        const auto = (op.dato.autos || []).find((a) => a && a._id)
        if (!auto) {
          this.aviso = `${this.nombre(op.dato.nombre) || 'Este cliente'} no tiene autos registrados.`
          return
        }
        return this.abrir(auto._id)
      }
      if (op.tipo === 'auto') return this.abrir(op.dato._id)
      const vehiculo = op.dato.vehiculo
      if (!vehiculo || !vehiculo._id) {
        this.aviso = op.dato.folio ? `La orden ${op.dato.folio} no tiene un auto ligado.` : 'Esta orden no tiene un auto ligado.'
        return
      }
      return this.abrir(vehiculo._id, op.dato._id)
    },
    rutaExpediente(vehiculo, orden) {
      return { name: 'historialVehiculo', params: { vehiculo: String(vehiculo) }, query: orden ? { orden: String(orden) } : {} }
    },
    hrefExpediente(vehiculo) {
      return this.$router.resolve(this.rutaExpediente(vehiculo)).href
    },
    alClicReciente(event, vehiculo) {
      if (!esClicSimple(event)) return
      event.preventDefault()
      this.abrir(vehiculo)
    },
    abrir(vehiculo, orden) {
      clearTimeout(this.temporizador)
      this.temporizador = null
      this.$router.push(this.rutaExpediente(vehiculo, orden)).catch(() => {})
    },
    nombre(valor) {
      return nombreLimpio(valor)
    },
    inicialesDe(valor) {
      return iniciales(valor)
    },
    descripcion(valor) {
      return descripcionDeAuto(valor)
    },
    oracion(valor) {
      return textoOracion(valor)
    },
    fecha(valor) {
      // received_at es fecha sin hora (medianoche UTC): se lee en UTC
      return formatearFechaNumerica(valor, { utc: true })
    },
    estadoTexto(estado) {
      return estadoDeOrden(estado).texto
    },
    tituloAuto(auto) {
      const color = textoOracion(auto.color).toLocaleLowerCase('es-MX')
      return [nombreDeAuto(auto), color].filter(Boolean).join(' · ')
    },
    metaAuto(auto) {
      const dueno = (auto.cliente && nombreLimpio(auto.cliente.nombre)) || 'Sin dueño registrado'
      const visitas = Number(auto.visitas) > 0 ? plural(Number(auto.visitas), 'visita', 'visitas') : 'Sin visitas'
      return `${dueno} · ${visitas}`
    },
    metaCliente(cliente) {
      const telefono = telefonosDe(cliente.telefonos)[0]
      const autos = (cliente.autos || []).filter(Boolean)
      const total = Math.max(Number(cliente.totalAutos) || 0, autos.length)
      let textoAutos = 'Sin autos registrados'
      if (total) {
        const nombres = autos.slice(0, 3).map((a) => descripcionDeAuto(a.descripcion))
        const resto = total - nombres.length
        textoAutos = plural(total, 'auto', 'autos')
        if (nombres.length) textoAutos += `: ${nombres.join(', ')}${resto > 0 ? ` y ${resto} más` : ''}`
      }
      return [telefono, textoAutos].filter(Boolean).join(' · ')
    },
  },
}
</script>
